/**
 * RecentAffairesQuickSelect Component
 * Displays 100 most recent affaires without appreciation data
 * Allows quick-select for auto-generation and opening affaire
 */

import React, { useState, useEffect } from 'react'
import { SparklesIcon, ClockIcon, FolderOpenIcon, ArrowRightIcon } from '@heroicons/react/24/outline'
import { useRecentAffairesWithoutAppreciation } from '../hooks/useIA'

interface RecentAffairesQuickSelectProps {
  onGenerateClick: (affaireId: number, adresse: string) => Promise<void>
  onOpenAffaire: (affaireId: number, adresse: string) => void
  generatingAffaires: Set<number>
}

export const RecentAffairesQuickSelect: React.FC<RecentAffairesQuickSelectProps> = ({
  onGenerateClick,
  onOpenAffaire,
  generatingAffaires
}) => {
  const { data: affaires = [], isLoading, error } = useRecentAffairesWithoutAppreciation()
  const [removingAffaires, setRemovingAffaires] = useState<Set<number>>(new Set())
  const [prevAffaireIds, setPrevAffaireIds] = useState<Set<number>>(new Set())
  const [isBulkGenerating, setIsBulkGenerating] = useState(false)

  // Detect when affaires are removed from the list (moved to approvals)
  useEffect(() => {
    const currentIds = new Set(affaires.map(a => a.affaireId))

    // Find affaires that were in the previous list but not in current
    const removedIds = Array.from(prevAffaireIds).filter(id => !currentIds.has(id))

    if (removedIds.length > 0) {
      // Add to removing set to trigger animation
      setRemovingAffaires(new Set(removedIds))

      // Remove from removing set after animation completes
      setTimeout(() => {
        setRemovingAffaires(new Set())
      }, 800) // Match animation duration
    }

    // Update previous IDs
    setPrevAffaireIds(currentIds)
  }, [affaires])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  // Bulk generation for first 10 affaires
  const handleBulkGenerate = async () => {
    const first10 = affaires.slice(0, 10)
    if (first10.length === 0) return

    setIsBulkGenerating(true)
    try {
      // Generate all 10 in parallel
      await Promise.all(
        first10.map(affaire =>
          onGenerateClick(affaire.affaireId, affaire.adresse)
        )
      )
    } finally {
      setIsBulkGenerating(false)
    }
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <p className="text-red-700 text-sm">
          Erreur lors du chargement des affaires récentes
        </p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl shadow-md border-2 border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-5 border-b border-indigo-200">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <ClockIcon className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Affaires récentes sans appréciation
              </h2>
              <p className="text-sm text-gray-600">
                {isLoading ? 'Chargement...' : `${affaires.length} affaires disponibles`}
              </p>
            </div>
          </div>

          {/* Bulk Generation Button */}
          {!isLoading && affaires.length > 0 && (
            <button
              onClick={handleBulkGenerate}
              disabled={isBulkGenerating}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 active:bg-indigo-800 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
            >
              {isBulkGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Génération en cours...
                </>
              ) : (
                <>
                  <SparklesIcon className="w-4 h-4" />
                  Générer les 10 premiers
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="max-h-[600px] overflow-y-auto">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-3 text-sm text-gray-500">Chargement des affaires...</p>
          </div>
        ) : affaires.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">Aucune affaire sans appréciation trouvée</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {affaires.map((affaire) => {
              const isGenerating = generatingAffaires.has(affaire.affaireId)
              const isRemoving = removingAffaires.has(affaire.affaireId)

              return (
                <div
                  key={affaire.affaireId}
                  className={`p-4 flex items-center justify-between gap-4 transition-all duration-700 ${
                    isRemoving
                      ? 'translate-x-full opacity-0 bg-green-50'
                      : 'translate-x-0 opacity-100 hover:bg-gray-50'
                  }`}
                  style={{
                    transitionProperty: 'transform, opacity, background-color',
                  }}
                >
                  <div className="flex-1 min-w-0 relative">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-500 flex-shrink-0">
                        #{affaire.affaireId}
                      </span>
                      <span className="text-sm text-gray-900 truncate">
                        {affaire.adresse}
                      </span>
                      {isRemoving && (
                        <span className="flex items-center gap-2 text-sm font-medium text-green-600 animate-pulse">
                          <ArrowRightIcon className="w-4 h-4" />
                          Vers Approbations
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Mis à jour: {formatDate(affaire.updatedAt || affaire.lastActivity || new Date().toISOString())}
                    </p>
                  </div>

                  <div className="flex-shrink-0 flex items-center gap-2">
                    <button
                      onClick={() => onOpenAffaire(affaire.affaireId, affaire.adresse)}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-all duration-150 shadow-sm"
                    >
                      <FolderOpenIcon className="w-4 h-4" />
                      Ouvrir
                    </button>

                    <button
                      onClick={() => onGenerateClick(affaire.affaireId, affaire.adresse)}
                      disabled={isGenerating}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 active:bg-indigo-800 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md min-w-[110px]"
                    >
                      {isGenerating ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          En cours...
                        </>
                      ) : (
                        <>
                          <SparklesIcon className="w-4 h-4" />
                          Générer
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
